import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { itemPercentage } from "../../utilis/common";
import { calculateDiffBetweenPercentages, pickComparisonColor } from "../../utilis/percentage-comparison";

const useDaysCard = ({
	data,
	comparisonData,
	defaultRadialData,
	page,
	filterListData,
	filterListDataComparison,
	filter,
	filterComparison,
	averageCensusComparison,
	averageCensus,
	reverseColors,
	projectionDays,
	cardTitle
}) => {
	const compareAgainstComparison = averageCensusComparison;
	const [dataList, setDataList] = useState([]);
	const [dataListBarChart, setDataListBarChart] = useState([]);
	const [dataListComparisonBarChart, setDataListComparisonBarChart] = useState([]);
	const percentageThresholds = useSelector((state) => state.comparison.percentageThresholds);

	const calculateLatestRedials = (ele, index, data, comparisonNumber = null) => {
		if (data && data.length > 0) {
			if (ele.parentId === "total") {
				const total = _.sumBy(data, "value");
				const percentageTotal = comparisonNumber
					? itemPercentage(total, comparisonNumber, "number")
					: itemPercentage(total, null, "number", page);
				const object = Object.assign({}, data[0], {
					y: total,
					percentage: ele.percentage ? ele.percentage : percentageTotal,
					color: ele.color,
				});
				return { ...ele, data: [object] };
			} else {
				const parentData = _.find(data, { key: ele.parentId });
				const total = parentData?.value ?? parentData?.total ?? 0;
				const percentageByValue = comparisonNumber
					? itemPercentage(total, comparisonNumber, "number", page)
					: itemPercentage(total, null, "number", page);
				const object = Object.assign({}, data[0], {
					y: total,
					percentage: ele.percentage ? ele.percentage : percentageByValue,
					color: ele.color,
				});
				return { ...ele, data: [object] };
			}
		} else {
			return defaultRadialData[index];
		}
	};

	const mainData = useMemo(() => {
		return data
			? data.map((item) => {
				return {
					...item,
					percentage: item?.percentage ? item.percentage : itemPercentage(item?.value ?? item?.total, null, "number", page), //itemPercentage(item.value, compareAgainst, "number", page),
				};
			})
			: [];
	}, [data, page]);

	const [radialDataList, setRadialDataList] = useState(
		mainData
			? defaultRadialData.map((ele, index) => calculateLatestRedials(ele, index, mainData, averageCensus))
			: defaultRadialData
	);
	const [radialDataComparisonList, setRadialDataComparisonList] = useState(defaultRadialData);

	useEffect(() => {
		// Use same order for both dataList and dataListBarChart to ensure alignment
		// Both should display items in the same order as the original data
		setDataList(mainData);
		setDataListBarChart(mainData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, page, percentageThresholds]);

	useEffect(() => {
		let calculationData;

		const radialDataListMain = mainData
			? defaultRadialData.map((ele, index) => calculateLatestRedials(ele, index, mainData, averageCensus))
			: defaultRadialData;
		setRadialDataList(radialDataListMain);

		if (comparisonData && comparisonData.length > 0) {
			const mainComparisonData = comparisonData.map((item) => ({
				...item,
				percentage: itemPercentage(item.value, compareAgainstComparison, "number", page),
			}));

			const latestRedialsComparison = radialDataComparisonList.map((ele, index) =>
				calculateLatestRedials(ele, index, mainComparisonData)
			);

			const radialDataListCompare = mainData
				? defaultRadialData.map((ele, index) => calculateLatestRedials(ele, index, mainData, compareAgainstComparison))
				: defaultRadialData;

				
			// Create comparison colors based on ID mapping
			const comparisonColorsByID = {};
			
			radialDataListCompare.forEach((x, i) => {
				const comparisonItem = latestRedialsComparison.find((y) => y.id === x.id);
				
				// Safety check: ensure both x and comparisonItem have data
				if (!x.data || !x.data[0] || !comparisonItem || !comparisonItem.data || !comparisonItem.data[0]) {
					return; // Skip this iteration if data is missing
				}
				
				// Find the original comparison data item to get the actual total/value
				// This ensures we use the same value structure as UserList.js (itemComparison?.total)
				// Use the original comparisonData instead of mainComparisonData to get the raw total
				const originalComparisonItem = comparisonData.find((item) => item._id === x.id || item.id === x.id);
				const comparisonTotal = originalComparisonItem?.total ?? originalComparisonItem?.value ?? comparisonItem.data[0].y;
				
				const percentage = itemPercentage(x.data[0].y, averageCensus, "number");
				const percentageComparison = itemPercentage(comparisonItem.data[0].y, averageCensusComparison, "number");
				calculationData = calculateDiffBetweenPercentages(
					percentage,
					percentageComparison,
					reverseColors,
					filter,
					filterComparison,
					comparisonTotal,
					projectionDays,
					cardTitle
				);
				
				comparisonColorsByID[x.id] = {
					comparisonColor: pickComparisonColor(calculationData.percentageDiff),
					comparingAgainstScaled: calculationData.itemTotalComparisonScaled,
					numberOfDays: calculationData?.numberOfDays,
				};
			});
			

			const updatedRadialsList = radialDataListCompare.map((x, i) => ({
				...x,
				data: [
					{
						...x.data[0],
						comparisonColor: comparisonColorsByID[x.id]?.comparisonColor,
						comparingAgainstScaled: comparisonColorsByID[x.id]?.comparingAgainstScaled,
						numberOfDays: comparisonColorsByID[x.id]?.numberOfDays,
					},
				],
			}));
	
			

			const updatedRedialDataComparisonList = latestRedialsComparison.map((x, i) => ({
				...x,
				data: [
					{
						...x.data[0],
						comparisonColor: comparisonColorsByID[x.id]?.comparisonColor,
						comparingAgainstScaled: comparisonColorsByID[x.id]?.comparingAgainstScaled,
						numberOfDays: comparisonColorsByID[x.id]?.numberOfDays,
					},
				],
			}));
			
			
			
			const mappedComparisonData = comparisonData.map((x, i) => {
				// Find the matching radial item by _id or id to get the correct comparison data
				// This ensures we match correctly even if the order is different
				// comparisonColorsByID is keyed by radialDataListCompare item's id
				const itemId = x._id || x.id || x.key;
				
				// The key insight: comparisonColorsByID is keyed by radialDataListCompare[i].id
				// So we need to find the radial item whose id matches the comparisonData item's id
				// Use a more precise match: only match on radialItem.id === itemId (exact match)
				let matchingRadialItem = null;
				let radialId = itemId; // Default to itemId if no match found
				
				// Try to find exact match by id
				for (const radialItem of radialDataListCompare) {
					if (radialItem.id === itemId) {
						matchingRadialItem = radialItem;
						radialId = radialItem.id;
						break;
					}
				}
				
				// If no match found by id, try by _id
				if (!matchingRadialItem && x._id) {
					for (const radialItem of radialDataListCompare) {
						if (radialItem.id === x._id || radialItem._id === x._id) {
							matchingRadialItem = radialItem;
							radialId = radialItem.id;
							break;
						}
					}
				}
				
				return {
					...x,
					comparisonColor: comparisonColorsByID[radialId]?.comparisonColor,
					comparingAgainstScaled: comparisonColorsByID[radialId]?.comparingAgainstScaled,
					numberOfDays: comparisonColorsByID[radialId]?.numberOfDays,
				};
			});		
			
			// Use same order as main data to ensure alignment
			setDataListComparisonBarChart(mappedComparisonData);
			setRadialDataList(updatedRadialsList);
			setRadialDataComparisonList(updatedRedialDataComparisonList);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [comparisonData, page, mainData, averageCensus, averageCensusComparison, percentageThresholds]);

	return {
		dataList,
		dataListBarChart,
		dataListComparisonBarChart,
		radialDataList,
		radialDataComparisonList,
	};
};

export default useDaysCard;
